import { MessageDto } from '../../types';
import { FileText, Image as ImageIcon, Mic, Video, FileQuestion } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';

interface MessageContentProps {
  message: MessageDto;
}

interface MessageContentData {
  text?: string;
  duration?: number;
  transcription?: string;
  url?: string;
  caption?: string;
  filename?: string;
  mimeType?: string;
}

export function MessageContent({ message }: MessageContentProps) {
  const content = message.content as MessageContentData;

  try {
    switch (message.type) {
      case 'TEXT':
        return <div className="whitespace-pre-wrap break-words">{content.text}</div>;

      case 'AUDIO':
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Mic className="h-4 w-4" />
              <span>Mensagem de áudio</span>
              {content.duration && (
                <span className="text-xs">({Math.floor(content.duration)}s)</span>
              )}
            </div>
            {content.transcription && (
              <div className="text-sm italic border-l-2 pl-2 border-muted">
                {content.transcription}
              </div>
            )}
            {!content.transcription && (
              <div className="text-xs text-muted-foreground">
                (Transcrição não disponível)
              </div>
            )}
          </div>
        );

      case 'IMAGE':
        return (
          <div className="space-y-2">
            {content.url ? (
              <div className="rounded-md overflow-hidden max-w-xs">
                <img
                  src={content.url}
                  alt={content.caption || 'Imagem'}
                  className="w-full h-auto"
                  loading="lazy"
                />
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <ImageIcon className="h-4 w-4" />
                <span>Imagem</span>
              </div>
            )}
            {content.caption && (
              <div className="text-sm">{content.caption}</div>
            )}
            {content.transcription && (
              <div className="text-sm italic border-l-2 pl-2 border-muted">
                Descrição: {content.transcription}
              </div>
            )}
          </div>
        );

      case 'DOCUMENT':
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {content.filename || 'Documento'}
                </div>
                {content.mimeType && (
                  <div className="text-xs text-muted-foreground">
                    {content.mimeType}
                  </div>
                )}
              </div>
            </div>
            {content.url ? (
              <a
                href={content.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline"
              >
                Fazer download
              </a>
            ) : (
              <div className="text-xs text-muted-foreground">
                (Download indisponível)
              </div>
            )}
            {content.caption && (
              <div className="text-sm">{content.caption}</div>
            )}
          </div>
        );

      case 'VIDEO':
        return (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Video className="h-4 w-4" />
            <span>Vídeo</span>
          </div>
        );

      default:
        return (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <FileQuestion className="h-4 w-4" />
            <span>Tipo de mensagem: {message.type}</span>
          </div>
        );
    }
  } catch (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Erro ao carregar conteúdo desta mensagem
        </AlertDescription>
      </Alert>
    );
  }
}
